import { Badge, Button, Group, Table, Text, Tooltip, useComputedColorScheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDateTime } from '../lib/format';
import {
  HOLAT_RANG, qurilmaAmallari, qurilmaTartibi, qurilmaXatoKaliti, type Qurilma, type QurilmaAmal,
} from '../lib/qurilma';

/** Xavfli amallar — tasdiq oynasi bilan (spec §8.5). */
const XAVFLI: QurilmaAmal[] = ['rad-etish', 'bloklash'];
const AMAL_RANG: Record<QurilmaAmal, string> = { tasdiqlash: 'teal', 'rad-etish': 'gray', bloklash: 'red', 'blokdan-chiqarish': 'blue' };

/**
 * Panel qurilmalari jadvali — maktab paneli (FACE ID → Qurilmalar) va superadmin "Qurilmalar"
 * sahifasi uchun BITTA joy. Tugmalar holat va huquqqa qarab (`qurilmaAmallari`); haqiqiy
 * tekshiruv serverda (`holatniOzgartir`).
 */
export function QurilmaJadvali({
  qurilmalar, huquq, yol, queryKeys, maktabUstuni = false,
}: {
  qurilmalar: Qurilma[];
  huquq: { tasdiqlay: boolean; bloklay: boolean };
  /** Amal manzili: maktab — `/qurilma/:id/:amal`, superadmin — `/superadmin/qurilmalar/:id/:amal`. */
  yol: (id: string, amal: QurilmaAmal) => string;
  /** Amaldan keyin yangilanadigan so'rovlar. */
  queryKeys: unknown[][];
  maktabUstuni?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const dark = useComputedColorScheme('light') === 'dark';
  // Superadmin jadvali kengroq (Maktab ustuni): 1280px atrofida kam muhim ustunlar (Terminal, Ulangan)
  // yashiriladi — amal tugmalari gorizontal skroll orqasida qolmasin (M12)
  const kamMuhim = maktabUstuni ? ({ visibleFrom: 'xl' } as const) : {};
  // Amallar ustuni o'ngda joyida turadi: uzun nom/maktab yoki tor ekranda jadval skrollansa ham tugmalar ko'rinadi.
  // Fon — karta foni (SectionCard: yorug'da body, qorong'ida dark-6); chap chiziq — ostidan o'tayotgan
  // ustun matni keskin kesilgandek ko'rinmasin
  const amalUstuni: React.CSSProperties = {
    position: 'sticky', right: 0, zIndex: 1, background: dark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-body)',
    boxShadow: 'inset 1px 0 0 var(--mantine-color-default-border)',
  };

  const yangila = () => queryKeys.forEach((queryKey) => qc.invalidateQueries({ queryKey }));

  const amal = useMutation({
    // Tasdiqda ro'yxatda ko'rilgan `holatAt` yuboriladi: orada ilova qayta ro'yxatdan o'tgan bo'lsa
    // (yangi kalit) server 409 `qurilmaOzgardi` beradi — tasdiq ko'rilmagan kalitga tegmaydi (M3, §6.0)
    mutationFn: ({ q, a }: { q: Qurilma; a: QurilmaAmal }) =>
      api.post(yol(q.id, a), a === 'tasdiqlash' ? { holatAt: q.holatAt } : undefined),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('qurilma.bajarildi') });
      yangila();
    },
    onError: (e: unknown) => {
      const r = (e as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
      const k = qurilmaXatoKaliti(r?.data);
      notifications.show({
        color: 'red',
        message: k ? t(k) : String(r?.data?.error ?? t('common.error')),
        autoClose: r?.status === 409 ? 7000 : undefined,
      });
      // 409 — qurilma ko'rilgandan beri o'zgargan (qayta ulandi / holati boshqa): eski ro'yxat bilan qayta urinilmasin
      if (r?.status === 409) yangila();
    },
  });

  const bos = (q: Qurilma, a: QurilmaAmal) => {
    if (!XAVFLI.includes(a)) return amal.mutate({ q, a });
    modals.openConfirmModal({
      title: t('qurilma.tasdiqSarlavha'),
      centered: true,
      children: (
        <>
          <Text size="sm"><b>{q.nom}</b> — {t('qurilma.tasdiqMatn')}</Text>
          {q.joriy && <Text size="sm" c="red" mt="xs">{t('qurilma.joriyOgohlantirish')}</Text>}
        </>
      ),
      labels: { confirm: t(`qurilma.amal.${a}`), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => amal.mutate({ q, a }),
    });
  };

  const tartibli = [...qurilmalar].sort(qurilmaTartibi);

  return (
    <Table.ScrollContainer minWidth={760}>
      <Table verticalSpacing="xs" style={{ whiteSpace: 'nowrap' }}>
        <Table.Thead>
          <Table.Tr>
            {maktabUstuni && <Table.Th>{t('qurilma.colMaktab')}</Table.Th>}
            <Table.Th>{t('qurilma.colNom')}</Table.Th>
            <Table.Th {...kamMuhim}>{t('qurilma.colTerminal')}</Table.Th>
            <Table.Th>{t('qurilma.colIp')}</Table.Th>
            <Table.Th>{t('qurilma.colFaollik')}</Table.Th>
            <Table.Th {...kamMuhim}>{t('qurilma.colYaratilgan')}</Table.Th>
            <Table.Th>{t('qurilma.colHolat')}</Table.Th>
            <Table.Th style={amalUstuni} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tartibli.map((q) => {
            const amallar = qurilmaAmallari(q.holat, huquq);
            const kalitsiz = q.holat === 'KUTILMOQDA' && !q.kalitBor;
            return (
              <Table.Tr key={q.id}>
                {maktabUstuni && <Table.Td>{q.tenant?.name ?? '—'}</Table.Td>}
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={500}>{q.nom}</Text>
                    {q.joriy && <Badge size="xs" variant="outline">{t('qurilma.shuKompyuter')}</Badge>}
                  </Group>
                  {kalitsiz && <Text size="xs" c="dimmed">{t('qurilma.kalitKutilmoqda')}</Text>}
                </Table.Td>
                <Table.Td {...kamMuhim}><Text size="sm">{q.terminal?.name ?? '—'}</Text></Table.Td>
                <Table.Td><Text size="xs" ff="monospace" c="dimmed">{q.oxirgiIp ?? q.ip ?? '—'}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{q.oxirgiFaollik ? formatDateTime(q.oxirgiFaollik) : '—'}</Text></Table.Td>
                <Table.Td {...kamMuhim}><Text size="xs" c="dimmed">{formatDateTime(q.yaratilgan)}</Text></Table.Td>
                <Table.Td><Badge color={HOLAT_RANG[q.holat]} variant="light" styles={{ root: { overflow: 'visible', maxWidth: 'none' }, label: { overflow: 'visible', textOverflow: 'clip' } }}>{t(`qurilma.holat.${q.holat}`)}</Badge></Table.Td>
                <Table.Td style={amalUstuni}>
                  <Group gap={6} wrap="nowrap" justify="flex-end">
                    {amallar.map((a) => {
                      const tugma = (
                        <Button
                          key={a}
                          size="compact-xs"
                          variant="light"
                          color={AMAL_RANG[a]}
                          disabled={amal.isPending || (a === 'tasdiqlash' && !q.kalitBor)}
                          onClick={() => bos(q, a)}
                        >
                          {t(`qurilma.amal.${a}`)}
                        </Button>
                      );
                      return a === 'blokdan-chiqarish'
                        ? <Tooltip key={a} label={t('qurilma.blokdanIzoh')} withArrow>{tugma}</Tooltip>
                        : tugma;
                    })}
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
